import { lights, uniform } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { AmbienceDef } from '../../engine/audio/Ambience';
import { CaveGlow } from '../../engine/caves/CaveGlow';
import { CaveInterior } from '../../engine/caves/CaveInterior';
import { CaveMesher } from '../../engine/caves/CaveMesher';
import { CaveField, nearestOnEntrance } from '../../engine/caves/caveField';
import { buildEntrances, type Entrance, entranceOptions, legacyPhaseB } from '../../engine/caves/entrance';
import {
  type CaveLayout,
  gateClips,
  generateInterior,
  type InteriorSpec,
  LOOK,
  type SpaceKind,
  strataOf,
} from '../../engine/caves/layout';
import type { FieldSample, V3 } from '../../engine/caves/sdf';
import type { PlayerController } from '../../engine/character/PlayerController';
import { flags } from '../../engine/core/flags';
import { GravityField } from '../../engine/gravity/GravityField';
import type { PhysicsWorld, StaticMesh } from '../../engine/physics/PhysicsWorld';
import { BackgroundWarmup } from '../../engine/render/BackgroundWarmup';
import { createInteriorMaterial } from '../../engine/render/materials/caveInteriorMaterial';
import { type CaveEnd, createCaveMaterial, DAYLIGHT_DEPTH } from '../../engine/render/materials/caveMaterial';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import { dirToFace, type FaceCoord } from '../../engine/terrain/cubeSphere';
import { buildMouthRocks, type MouthRockStyle, mouthRocksCollision } from '../../engine/terrain/MouthRocks';
import { lipDepthAt, type TerrainMouth } from '../../engine/terrain/TerrainMouth';
import { type HollowDef, makeHollow } from '../../engine/universe/HollowDef';
import { LoadPolicy } from '../../engine/universe/LoadPolicy';
import { buildStalactites } from '../../engine/universe/Stalactites';
import {
  buildTube,
  perpendicularBasis,
  type SpiralOptions,
  type TunnelMouth,
} from '../../engine/universe/Tunnel';
import { linkGaps, linkSeed, placeHollows, plateauSize } from './cavePlacement';
import type { GameWorld } from './GameWorld';
import { HollowScene, type HollowSceneDef } from './HollowScene';

type RGB = [number, number, number];

/** One end of a cave: where it opens into a hollow. */
export interface CaveEndDef {
  /** Ground height of the mouth's plateau (m above sea level; close to the natural ground there). */
  height: number;
  /** Light spilling in at this mouth, and the lichen colour near this end. */
  leak: RGB;
  glow: RGB;
  /** Boulders around the mouth. */
  rocks: MouthRockStyle;
}

/** A cave from hollow `from` to hollow `to` (indices into `CaveDef.hollows`). */
export interface CaveLinkDef {
  from: number;
  to: number;
  /** Direction from `from`'s centre toward `to` (hollow frame). `to` is placed along it. */
  axis: RGB;
  /**
   * Where the mouths are. Each cave used to be one spiral (`tunnel`) through `spiralGap`
   * metres of rock; the mouths, and the ground shaped around them, stay where those spirals
   * put them (see legacyPhaseB). The worlds themselves now sit further apart (linkGaps),
   * with a generated cave between the two entrances.
   */
  tunnel: SpiralOptions;
  spiralGap: number;
  /** Where round the axis mouth B is (radians), instead of where the old spiral ended. */
  phaseB?: number;
  /** Metres of rock between the two worlds (default: by their sizes, see caveGap). */
  gap?: number;
  /** The end in `from`, and the end in `to`. */
  a: CaveEndDef;
  b: CaveEndDef;
}

/**
 * Terrain hollows in one universe, joined by caves. The first hollow keeps its own
 * centre; each link places its `to` hollow beyond `from`.
 */
export interface CaveDef {
  id: string;
  name: string;
  hollows: HollowSceneDef[];
  links: CaveLinkDef[];
  hints?: string[];
}

/** One way down into a cave, with what the zone tests need. */
interface EntranceInfo {
  entrance: Entrance;
  /** A sphere around all of it, for a quick first test. */
  center: THREE.Vector3;
  bound2: number;
  /** Metres from its top to its gate. */
  length: number;
}

/** A disc across an entrance's gate: shut (drawn and solid) until the interior behind it is built. */
interface GateCap {
  mesh: THREE.Mesh;
  anchor: THREE.Vector3;
  positions: Float32Array;
  indices: Uint32Array;
  body: StaticMesh | null;
}

/** A cave between two hollows: a fixed entrance at each end and a generated interior between their gates. */
interface Link {
  def: CaveLinkDef;
  name: string;
  /** Mouths in `from` and in `to` (hollow-local). */
  mouths: [TerrainMouth, TerrainMouth];
  ends: [CaveEnd, CaveEnd];
  entrances: [EntranceInfo, EntranceInfo];
  layout: CaveLayout;
  /** The cave's own gravity (a region of the world's field), and its shape. */
  cave: CaveField;
  /** Universe position of the interior's origin (halfway between the gates). */
  origin: THREE.Vector3;
  originRel: { value: THREE.Vector3 };
  /** The two gates (cave-local). */
  gates: [V3, V3];
  interior: CaveInterior;
  /** Its crystals and glowing fungi, and their light (in the light scene). */
  glow: CaveGlow;
  /** The gates' caps, and the mouths (universe), whose distance decides when the interior loads. */
  caps: [GateCap, GateCap];
  mouthPoints: [THREE.Vector3, THREE.Vector3];
  /** The interior's route (cave-local, flat), metres along it to each point, and in all. */
  route: Float64Array;
  routeAt: Float32Array;
  routeLength: number;
  /** The space each of the layout's voids is. */
  spaceOfVoid: Int32Array;
  /** Mouth A to mouth B (universe): down entrance A, the interior's route, up entrance B. For scripted walks. */
  walk: THREE.Vector3[];
  rocks: THREE.InstancedMesh[];
  /** The entrances, their stalactites and the interior (the boulders belong to each hollow's own group). */
  group: THREE.Group;
  /** Collision: the entrances and the boulders at both ends (the interior has its own). */
  bodies: StaticMesh[];
}

/** Where in a cave a point is (see caveAt). */
interface CaveHit {
  link: number;
  /** 0 or 1: down that end's entrance; −1: in the interior. */
  end: number;
  /** Entrance: how far along its path (in path indices). Interior: the nearest route point (−1 if not asked). */
  at: number;
  /** Entrance: depth below its mouth's ground (m; negative above it). */
  depth: number;
  /** Interior: the space it's in. */
  space: number;
}

/**
 * The lip around each hole, in tube radii: how wide it is and how far it dips at the edge.
 * Gentle enough that the terrain's 1 m triangles follow its curve to within a centimetre.
 */
const LIP_WIDTH = 1.6;
const LIP_DEPTH = 0.3;
/**
 * Past its gate an entrance runs on a little, a few centimetres wider: hidden behind the
 * interior's walls, it fills any gap where they're cut off at the gate.
 */
const GATE_OVERLAP = 0.6;
const GATE_FLARE = 0.06;
/** "Depth" in an interior, for the lantern, exposure and sound: deep. */
const INTERIOR_DEPTH = 50;
/** Where along an interior (0 … 1) the other world takes over, going each way. */
const SWITCH_LOW = 0.45;
const SWITCH_HIGH = 0.55;
/** Milliseconds per frame for turning meshed cave chunks into meshes and colliders. */
const BUILD_BUDGET = 3;
/**
 * A cave's interior is built when you come within LOAD_NEAR metres of one of its mouths (or
 * are in it), and dropped (its meshed chunks kept) once you're UNLOAD_FAR from both, or in a
 * world it doesn't open into.
 */
const LOAD_NEAR = 120;
const UNLOAD_FAR = 260;

const _down = new THREE.Vector3();
/** Distance from its hollow's centre to the ground at a cave end, under universe point `p`. */
function groundRadius(e: CaveEnd, p: THREE.Vector3, center: THREE.Vector3): number {
  _down.subVectors(p, center).normalize();
  return e.plateauRadius + lipDepthAt(e.mouth, e.plateauRadius, _down);
}
/** Exposure deep in a cave; each hollow's own exposure takes over near its mouth. */
const CAVE_EXPOSURE = 1.5;
/** How deep down an entrance (m) the eye has fully adapted to the cave. */
const ADAPT_DEPTH = 18;
/** Deep in a cave: drips with echo, a low drone and a breath of air. */
const CAVE_AMBIENCE: AmbienceDef = {
  wind: { gain: 0.06, cutoff: 220 },
  drone: { freqs: [49, 73.4], gain: 0.014 },
  events: [{ kind: 'drip', rate: 0.45, gain: 0.06 }],
  echo: 0.55,
};
const SPACE_NAMES: Record<SpaceKind, string> = {
  passage: 'passage',
  chamber: 'chamber',
  cavern: 'cavern',
  zeroG: 'weightless cavern',
  grotto: 'glowing grotto',
  crack: 'chasm',
  shaft: 'drifting shaft',
};

const _g = new THREE.Vector3();
const _hit: CaveHit = { link: -1, end: -1, at: 0, depth: 0, space: -1 };
const _sample: FieldSample = { phi: 0, s: 0, a: 1, rho: 1, owner: -1 };
const _fc: FaceCoord = { face: 0, u: 0, v: 0 };

/**
 * Terrain hollows in one universe, joined by caves (M6). Only the hollow you're in
 * is drawn; the others wait with their lights dimmed. Each cave is a fixed entrance at
 * each end (a spiral stair down from the mouth that hides the rest from view) and a
 * generated interior between their gates: passages, rooms, caverns, grottos. In the
 * interior every wall is a floor. The switch between worlds happens deep in the
 * interior, where neither is in view, so walking from one world to the next has no
 * loading screen and no visible seam.
 */
export class CaveWorld implements GameWorld {
  readonly name: string;
  readonly hollows: HollowDef[];
  readonly field = new GravityField();
  readonly scenes: HollowScene[];
  readonly hints?: string[];
  readonly links: Link[];
  /** Mouths on each hollow's wall. */
  private readonly mouthsOf: TerrainMouth[][];
  private readonly mesher = new CaveMesher();
  private active = 0;
  /** Hollows whose shaders are still to be started in the background (see warmInBackground). */
  private warmQueue: number[] | null = null;
  private warming: { index: number; warm: BackgroundWarmup; caves: THREE.Group[] } | null = null;
  private exposure = 0;
  /** Depth of the player below the nearest mouth plateau while in a cave (m), else 0. */
  private caveDepth = 0;
  /** Where the player is: which cave, if any (see caveAt). */
  private readonly zone: CaveHit = { link: -1, end: -1, at: 0, depth: 0, space: -1 };
  private autoLantern = false;
  private steps = 0;
  /** Which worlds' data is in memory (see LoadPolicy), and the world the player waits for. */
  private readonly policy = new LoadPolicy();
  private waitingFor: number | null = null;
  /** Hollows whose compute shaders are still to be compiled in the background. */
  private precompileQueue: number[] = [];
  private precompiling = false;
  /** Deep enough in a cave for its own soundscape (with some hysteresis). */
  private underground = false;
  /** Every cave mouth in universe space, with its hollow and where it leads. */
  private readonly mouthInfo: { hollow: number; point: THREE.Vector3; dir: THREE.Vector3; to: string }[] = [];

  constructor(
    readonly def: CaveDef,
    private readonly origin: RenderOrigin,
    scene: THREE.Scene,
    renderer: THREE.WebGPURenderer,
  ) {
    this.name = def.name;
    this.hints = def.hints;
    const H = def.hollows;
    const gaps = linkGaps(def);
    const centers = placeHollows(def, gaps);

    // Entrances meet each hollow at its mouth plateau, a sphere of radius R − height.
    const plateau = (i: number, h: number) =>
      makeHollow({
        id: H[i].id,
        name: H[i].name,
        center: [centers[i].x, centers[i].y, centers[i].z],
        radius: H[i].radius - h,
        seed: H[i].seed,
        sun: H[i].sun,
      });
    const toTerrain = (m: TunnelMouth, c: THREE.Vector3, e: CaveEndDef, R: number): TerrainMouth => ({
      dir: m.point.clone().sub(c).normalize(),
      height: e.height,
      ...plateauSize(R),
      point: m.point.clone().sub(c),
      axis: m.dir.clone(),
      radius: m.radius,
      lipWidth: LIP_WIDTH * m.radius,
      lipDepth: LIP_DEPTH * m.radius,
    });
    this.mouthsOf = H.map(() => []);
    const ways = def.links.map((l) => {
      const [Ra, Rb] = [H[l.from].radius, H[l.to].radius];
      const r = l.tunnel.tubeRadius;
      const entrances = buildEntrances(
        plateau(l.from, l.a.height),
        plateau(l.to, l.b.height),
        [Ra, Rb],
        { ...l.tunnel, phaseB: l.phaseB ?? legacyPhaseB(l.tunnel, l.spiralGap, l.a.height, l.b.height) },
        [entranceOptions(Ra, r), entranceOptions(Rb, r)],
      );
      const mouths: [TerrainMouth, TerrainMouth] = [
        toTerrain(entrances[0].mouth, centers[l.from], l.a, Ra),
        toTerrain(entrances[1].mouth, centers[l.to], l.b, Rb),
      ];
      this.mouthsOf[l.from].push(mouths[0]);
      this.mouthsOf[l.to].push(mouths[1]);
      return { entrances, mouths };
    });
    this.scenes = H.map(
      (h, i) => new HollowScene(h, origin, scene, renderer, { center: centers[i], mouths: this.mouthsOf[i] }),
    );
    this.hollows = this.scenes.map((s) => s.hollow);
    // Each hollow's push into the rock reaches as far as its nearest neighbour's wall (joined
    // by a cave or not: a world at the end of a long cave may lie closer to another).
    this.hollows.forEach((h, i) => {
      const reach = def.links.flatMap((l, li) => (l.from === i || l.to === i ? [gaps[li]] : []));
      this.hollows.forEach((o, j) => {
        if (j !== i) reach.push(o.center.distanceTo(h.center) - o.radius - h.radius);
      });
      this.field.add({
        center: h.center,
        radius: h.radius,
        strength: h.gravity,
        reach: reach.length > 0 ? Math.min(...reach) : undefined,
      });
    });

    // The caves' interiors, one after another: each keeps clear of the worlds, of every
    // entrance near it, and of the caves generated before it.
    const variant = flags.num('caveseed', 0);
    const taken: { c: THREE.Vector3; r: number }[] = [];
    const interiors = def.links.map((l, li) => {
      const ends = ways[li].entrances;
      const center = ends[0].gate.clone().add(ends[1].gate).multiplyScalar(0.5);
      const local = (v: THREE.Vector3): V3 => [v.x - center.x, v.y - center.y, v.z - center.z];
      const dir = (v: THREE.Vector3): V3 => [v.x, v.y, v.z];
      const around = ends[0].gate.distanceTo(ends[1].gate) / 2 + 150;
      const near = (c: THREE.Vector3, r: number) => c.distanceTo(center) < around + r;
      const spec: InteriorSpec = {
        seed: linkSeed(def, li, variant),
        gates: [0, 1].map((k) => ({
          p: local(ends[k].gate),
          dir: dir(ends[k].gateDir),
          down: dir(ends[k].down),
          radius: ends[k].radius,
          g: this.hollows[k === 0 ? l.from : l.to].gravity,
        })) as InteriorSpec['gates'],
        avoid: [
          ...this.hollows.map((h) => ({ c: local(h.center), r: h.radius })),
          ...taken.filter((t) => near(t.c, t.r)).map((t) => ({ c: local(t.c), r: t.r })),
        ],
        avoidTubes: ways
          .flatMap((w, wi) => (wi === li ? w.entrances : w.entrances.filter((e) => near(e.gate, 80))))
          .map((e) => ({ points: e.path.slice(e.helixStart).map(local), radius: e.radius })),
      };
      const layout = generateInterior(spec);
      for (const v of layout.shape.voids) {
        if (v.kind === 'ellipsoid') {
          const c = new THREE.Vector3(...v.center).add(center);
          taken.push({ c, r: Math.max(...v.radii) + v.rough });
        } else {
          for (let i = 0; i < v.points.length; i += 2) {
            taken.push({ c: new THREE.Vector3(...v.points[i]).add(center), r: v.radii[i] + v.rough + 1 });
          }
        }
      }
      return { layout, center, spec };
    });

    // The caves: rock tubes and walls lit by whichever lantern is lit, boulders at the mouths.
    const lanterns = lights(this.scenes.map((s) => s.lantern));
    const small = (l: CaveLinkDef) => Math.max(H[l.from].radius, H[l.to].radius) < 2000;
    this.links = def.links.map((l, li): Link => {
      const { entrances, mouths } = ways[li];
      const { layout, center, spec } = interiors[li];
      const name = `${H[l.from].name} → ${H[l.to].name}`;
      // The cave's gravity: each gate's stub pulls as the first (or last) passage does.
      const pullAt = (k: 0 | 1) => {
        const rs = layout.routeSpace;
        const sp = layout.spaces[rs[k === 0 ? 0 : rs.length - 1]];
        return layout.shape.voids[sp.void].pull ?? { s: 0, a: 1 };
      };
      const cave = new CaveField(layout, center, [
        { entrance: entrances[0], pull: pullAt(0) },
        { entrance: entrances[1], pull: pullAt(1) },
      ]);
      // ?cavegravity=0: only the worlds' own gravity in caves (for comparison).
      if (flags.bool('cavegravity', true)) this.field.addRegion(cave);

      // The entrances end where they meet the ground at the mouth (the plateau and its lip).
      const end = (i: number, e: CaveEndDef, m: TerrainMouth): CaveEnd => ({
        centerRel: uniform(new THREE.Vector3()),
        plateauRadius: H[i].radius - e.height,
        mouth: m,
        ground: {
          style: this.scenes[i].style,
          profile: H[i].terrain,
          radius: H[i].radius,
          time: this.scenes[i].terrain.time,
        },
        // (With the lava's own light, as the ground beside it gets it.)
        daylight: (local, n) => this.scenes[i].rig.daylightAt(local, n, true),
        groundCells: uniform(this.scenes[i].terrain.cellsAt(this.scenes[i].tree.maxLevel)),
        groundMorph: uniform(0),
        light: uniform(1),
        leak: new THREE.Color(...e.leak),
        glow: new THREE.Color(...e.glow),
      });
      const ends: [CaveEnd, CaveEnd] = [end(l.from, l.a, mouths[0]), end(l.to, l.b, mouths[1])];
      const originRel = uniform(new THREE.Vector3());
      const look = {
        originRel,
        gateA: spec.gates[0].p,
        gateB: spec.gates[1].p,
        glowA: new THREE.Color(...l.a.glow),
        glowB: new THREE.Color(...l.b.glow),
        strata: strataOf(spec),
      };
      const tubeMaterial = createCaveMaterial(ends, lanterns, 1, { ...look, look: LOOK.passage });
      const group = new THREE.Group();
      group.name = name;
      scene.add(group);
      // Full stalactite length at 80% of the worlds' surface gravity, as when that was 10 m/s².
      const fullAt = 0.8 * Math.min(this.hollows[l.from].gravity, this.hollows[l.to].gravity);
      entrances.forEach((e, k) => {
        const tube = entranceTube(e);
        const anchor = tube.path[tube.path.length >> 1].clone();
        const data = buildTube(tube.path, tube.radii, 28, anchor);
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
        geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
        geo.setAttribute('uv', new THREE.BufferAttribute(data.uvs, 2));
        geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
        geo.computeBoundingSphere();
        const mesh = new THREE.Mesh(geo, tubeMaterial);
        mesh.frustumCulled = false;
        mesh.name = `${name} (${H[k === 0 ? l.from : l.to].name} entrance)`;
        group.add(mesh);
        origin.anchor(mesh, anchor);
        // Stalactites only where the roof is past the daylight: the lanterns are all that light them.
        const si = k === 0 ? l.from : l.to;
        const outside = (p: THREE.Vector3) =>
          p.distanceTo(centers[si]) < groundRadius(ends[k], p, centers[si]) + e.radius + DAYLIGHT_DEPTH;
        const drips = buildStalactites(
          { path: e.path, radius: e.radius, name: mesh.name },
          this.field,
          anchor,
          lanterns,
          outside,
          11 + li * 13 + k * 7,
          fullAt,
        );
        group.add(drips);
        origin.anchor(drips, anchor);
      });

      const caps = entrances.map((e): GateCap => {
        const cap = gateCap(e);
        const mesh = new THREE.Mesh(cap.geometry, tubeMaterial);
        mesh.frustumCulled = false;
        mesh.name = `${name} gate`;
        group.add(mesh);
        origin.anchor(mesh, e.gate);
        return { mesh, anchor: e.gate.clone(), positions: cap.positions, indices: cap.indices, body: null };
      }) as [GateCap, GateCap];

      const interior = new CaveInterior(
        `${def.id}-${li}`,
        // ?cavecell=0.35: finer (or coarser) walls, in metres.
        {
          shape: layout.shape,
          clips: gateClips(spec),
          cell: flags.num('cavecell', small(l) ? 0.4 : 0.5),
          n: 32,
        },
        center,
        createInteriorMaterial({ ...look, lights: lanterns }),
        this.mesher,
        origin,
      );
      group.add(interior.group);
      const glow = new CaveGlow(lanterns);
      interior.glow = glow;
      interior.group.add(glow.group);
      origin.anchor(glow.lights, center);

      const rocks = [l.from, l.to].map((si, k) => {
        const s = this.scenes[si];
        const style = k === 0 ? l.a.rocks : l.b.rocks;
        const r = buildMouthRocks(mouths[k], s.hollow.radius, style, s.ownLights, 7 + li * 57 + k * 31);
        s.group.add(r);
        origin.anchor(r, s.hollow.center.clone().add(mouths[k].point));
        return r;
      });

      const route = new Float64Array(layout.route.flat());
      const routeAt = new Float32Array(layout.route.length);
      for (let i = 1; i < layout.route.length; i++) {
        const [a, b] = [layout.route[i - 1], layout.route[i]];
        routeAt[i] = routeAt[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
      }
      const spaceOfVoid = new Int32Array(layout.shape.voids.length).fill(-1);
      layout.spaces.forEach((s, i) => {
        spaceOfVoid[s.void] = i;
      });
      const walk = [
        ...entrances[0].path.map((p) => p.clone()),
        ...layout.route.slice(1, -1).map((q) => new THREE.Vector3(...q).add(center)),
        ...entrances[1].path.map((p) => p.clone()).reverse(),
      ];
      return {
        def: l,
        name,
        mouths,
        ends,
        entrances: [entranceInfo(entrances[0]), entranceInfo(entrances[1])],
        layout,
        cave,
        origin: center,
        originRel,
        gates: [spec.gates[0].p, spec.gates[1].p],
        interior,
        glow,
        caps,
        mouthPoints: [
          this.hollows[l.from].center.clone().add(mouths[0].point),
          this.hollows[l.to].center.clone().add(mouths[1].point),
        ],
        route,
        routeAt,
        routeLength: routeAt[routeAt.length - 1],
        spaceOfVoid,
        walk,
        rocks,
        group,
        bodies: [],
      };
    });
    for (const link of this.links) {
      const ends = [link.def.from, link.def.to];
      ends.forEach((si, k) => {
        const m = link.mouths[k];
        this.mouthInfo.push({
          hollow: si,
          point: this.hollows[si].center.clone().add(m.point),
          dir: m.dir,
          to: this.hollows[ends[1 - k]].name,
        });
      });
    }
    this.exposure = H[0].exposureKey;
    // Only the hollow you're in, and the caves out of it, are drawn; the others' shaders
    // warm up after boot.
    // Only the first world's data is loaded at boot; the others are shells until the
    // player comes near (see streamWorlds). Dropping them now, before anything has drawn,
    // means their terrain atlases are never allocated.
    this.active = this.home;
    for (const [i, s] of this.scenes.entries()) {
      s.setActive(i === this.active);
      if (i !== this.active) s.unload();
    }
    this.showCaves();
  }

  private get current(): HollowScene {
    return this.scenes[this.active];
  }

  get atmosphere() {
    return this.current.atmosphere;
  }

  get viewRadius() {
    return this.current.hollow.radius;
  }

  get clouds() {
    return this.current.clouds;
  }

  get rig() {
    return this.current.rig;
  }

  get currentExposure(): number {
    return this.exposure;
  }

  get ambience(): AmbienceDef | undefined {
    // More than a few metres underground, the cave's own sound takes over.
    return this.underground ? CAVE_AMBIENCE : this.current.ambience;
  }

  /**
   * The echo where the player is: none outside; growing down an entrance; in the interior,
   * longer and fuller in bigger spaces (the time to the far wall and back).
   */
  echo(): { amount: number; delay: number } | null {
    const z = this.zone;
    if (z.link < 0) return null;
    if (z.end >= 0) return { amount: 0.35 * smooth(2, 14, this.caveDepth), delay: 0.12 };
    const size = this.links[z.link].layout.spaces[z.space]?.size ?? 4;
    return {
      amount: 0.3 + 0.3 * smooth(4, 24, size),
      delay: Math.min(Math.max((2 * size) / 343, 0.04), 0.4),
    };
  }

  get lightGroups(): THREE.Object3D[] {
    return [...this.scenes.flatMap((s) => s.lightGroups), ...this.links.map((l) => l.glow.lights)];
  }

  /** Every hollow's sky (for building post pipelines up front). */
  get skies() {
    return this.scenes.map((s) => ({ atmosphere: s.atmosphere, clouds: s.clouds ?? null }));
  }

  /** The world the game starts in: the first cave's `from`. */
  private get home(): number {
    return this.def.links[0]?.from ?? 0;
  }

  async prepare(): Promise<void> {
    await this.scenes[this.home].prepare();
  }

  initPhysics(physics: PhysicsWorld): void {
    for (const s of this.scenes) s.initPhysics(physics);
    for (const link of this.links) {
      const ends = [link.def.from, link.def.to];
      // Each entrance's collision, cut off where it comes up out of its mouth plateau.
      link.entrances.forEach(({ entrance }, k) => {
        const tube = entranceTube(entrance);
        const anchor = tube.path[tube.path.length >> 1].clone();
        const data = buildTube(tube.path, tube.radii, 20, anchor);
        const c = this.hollows[ends[k]].center;
        const p = new THREE.Vector3();
        const depth = (x: number, y: number, z: number) => {
          p.set(x, y, z).add(anchor);
          return p.distanceTo(c) - groundRadius(link.ends[k], p, c);
        };
        const solid = clipBelow(data.positions, data.indices, depth);
        link.bodies.push(physics.addStaticMesh(anchor, solid.positions, solid.indices));
      });
      link.rocks.forEach((rocks, k) => {
        const c = mouthRocksCollision(rocks);
        link.bodies.push(
          physics.addStaticMesh(
            this.hollows[ends[k]].center.clone().add(link.mouths[k].point),
            c.positions,
            c.indices,
          ),
        );
      });
      link.interior.setPhysics(physics);
      for (const cap of link.caps) cap.body = physics.addStaticMesh(cap.anchor, cap.positions, cap.indices);
    }
    this.showCaves();
  }

  /** True if `link` opens into hollow `i`. */
  private static opensInto(link: Link, i: number): boolean {
    return link.def.from === i || link.def.to === i;
  }

  /**
   * Only the caves that open into the hollow you're in are drawn and solid; the rest wait,
   * built but switched off. Their interiors are meshed as they come into play (and kept).
   * Switching happens deep in a cave, where the caves beyond are out of sight.
   */
  private showCaves(): void {
    for (const link of this.links) {
      const on = CaveWorld.opensInto(link, this.active);
      // (A cave whose shaders are warming stays drawn until they're done: see stepWarmup.)
      link.group.visible = on || (this.warming?.caves.includes(link.group) ?? false);
      link.glow.lights.visible = link.group.visible;
      for (const b of link.bodies) if (b.body.isEnabled() !== on) b.body.setEnabled(on);
    }
    this.shutGates();
  }

  /** Each gate stays shut (drawn and solid) until the interior behind it is built. */
  private shutGates(): void {
    for (const link of this.links) {
      const open = link.interior.state === 'ready';
      const solid = !open && CaveWorld.opensInto(link, this.active);
      for (const cap of link.caps) {
        cap.mesh.visible = !open;
        if (cap.body && cap.body.body.isEnabled() !== solid) cap.body.body.setEnabled(solid);
      }
    }
  }

  /**
   * Load the worlds the player is in or near, and drop the ones left behind (LoadPolicy).
   * The world drawn is always among those wanted.
   */
  private streamWorlds(p: THREE.Vector3): void {
    const z = this.zone;
    const { load, unload } = this.policy.update({
      place: z.link >= 0 ? { link: z.link } : { hollow: this.active },
      links: this.links.map((l) => ({
        from: l.def.from,
        to: l.def.to,
        mouthDistance: [p.distanceTo(l.mouthPoints[0]), p.distanceTo(l.mouthPoints[1])],
      })),
      now: performance.now() / 1000,
    });
    for (const i of load) void this.scenes[i].load();
    for (const i of unload) if (i !== this.active) this.scenes[i].unload();
  }

  /** While the game runs: compile the next hollow's compute shaders, one hollow at a time. */
  private stepPrecompile(): void {
    if (this.precompiling) return;
    const i = this.precompileQueue.shift();
    if (i === undefined) return;
    this.precompiling = true;
    void this.scenes[i]
      .precompile()
      .catch((e) => console.warn('[innerworlds] precompile', e))
      .finally(() => {
        this.precompiling = false;
      });
  }

  /** "Loading Glowmire…" while the player waits for a world or a cave, for the HUD. */
  loadingNote(): string | null {
    if (this.waitingFor !== null) return `Loading ${this.hollows[this.waitingFor].name}…`;
    const z = this.zone;
    if (z.link >= 0 && z.end < 0 && this.links[z.link].interior.state !== 'ready') return 'Loading the cave…';
    return null;
  }

  /**
   * Build the interiors of the caves you're in or near, nearest gate first, and drop the ones
   * you've left behind (their meshed chunks stay cached, so coming back only rebuilds them).
   */
  private streamInteriors(p: THREE.Vector3): void {
    this.links.forEach((link, li) => {
      const i = link.interior;
      const near = Math.min(p.distanceTo(link.mouthPoints[0]), p.distanceTo(link.mouthPoints[1]));
      const on = CaveWorld.opensInto(link, this.active);
      if (this.zone.link === li || (on && near < LOAD_NEAR)) {
        if (i.state === 'unloaded') {
          const [a, b] = link.gates;
          const da = _g
            .set(...a)
            .add(link.origin)
            .distanceTo(p);
          const db = _g
            .set(...b)
            .add(link.origin)
            .distanceTo(p);
          i.load(da <= db ? a : b);
        }
      } else if (i.state !== 'unloaded' && (!on || near > UNLOAD_FAR)) {
        i.unload();
      }
    });
  }

  /** Start on the first hollow's plateau, a little way from the first cave mouth, looking into it. */
  async spawn(player: PlayerController): Promise<void> {
    const first = this.links[0];
    const home = first.def.from;
    if (this.active !== home) this.switchTo(home);
    const m = first.mouths[0];
    const h = this.hollows[home];
    const horiz = m.axis.clone().addScaledVector(m.dir, -m.axis.dot(m.dir)).normalize();
    const dir = m.point.clone().addScaledVector(horiz, -26).normalize();
    const face = h.center.clone().add(m.point).addScaledVector(horiz, 8);
    await this.scenes[home].spawn(player, { dir, face });
  }

  update(player: PlayerController): void {
    const side = this.sideOf(player.position);
    if (side !== this.active) {
      this.switchTo(side);
      // The first time you come up into a world, it's mid-morning there too.
      this.current.arrive(_g.copy(player.position).sub(this.current.hollow.center).normalize());
    }
    if (this.steps++ % 15 === 0) {
      this.streamWorlds(player.position);
      this.streamInteriors(player.position);
    }
    // Teleported (or flown) somewhere that isn't there yet: wait for it. Into a cave that
    // isn't built, until its walls are; into a world that isn't loaded, until the ground
    // under you has collision.
    const z = this.zone;
    const inCave = z.link >= 0 && z.end < 0 && this.links[z.link].interior.state !== 'ready';
    if (z.link < 0 && this.current.loadState !== 'loaded') this.waitingFor = this.active;
    if (
      this.waitingFor !== null &&
      (this.waitingFor !== this.active || this.current.groundReadyAt(player.position))
    ) {
      this.waitingFor = null;
    }
    player.frozen = player.mode !== 'fly' && (inCave || this.waitingFor !== null);
    if (this.caveDepth > 10) this.underground = true;
    else if (this.caveDepth < 5) this.underground = false;
    // The lantern lights itself a few metres into a cave, and goes out again when
    // you climb out (unless you lit it yourself).
    if (this.caveDepth > 3 && !this.current.lanternLit) {
      this.current.setLantern(true);
      this.autoLantern = true;
    } else if (this.caveDepth < 1.5 && this.autoLantern) {
      this.current.setLantern(false);
      this.autoLantern = false;
    }
    this.current.update(player);
  }

  /**
   * Which world the player belongs to: the hollow they're in, the one whose entrance
   * they're in, or the nearer half of a cave's interior (switching a little past the
   * middle, so walking to and fro there doesn't flip it). Sets the zone and caveDepth.
   */
  private sideOf(p: THREE.Vector3): number {
    const hit = this.caveAt(p, 1.5, true);
    if (!hit) {
      this.zone.link = -1;
      this.caveDepth = 0;
      for (let i = 0; i < this.hollows.length; i++) {
        const h = this.hollows[i];
        if (p.distanceTo(h.center) < h.radius + 50) return i;
      }
      return this.active;
    }
    Object.assign(this.zone, hit);
    const link = this.links[hit.link];
    const [a, b] = [link.def.from, link.def.to];
    if (hit.end >= 0) {
      this.caveDepth = Math.max(0, hit.depth);
      return hit.end === 0 ? a : b;
    }
    this.caveDepth = INTERIOR_DEPTH;
    const t = link.routeAt[hit.at] / link.routeLength;
    if (t < SWITCH_LOW) return a;
    if (t > SWITCH_HIGH) return b;
    // Near the middle: stay, unless this cave doesn't open into the current world at all.
    if (this.active === a || this.active === b) return this.active;
    return t < 0.5 ? a : b;
  }

  /**
   * The cave a universe position is in, if any: down an entrance (within `margin` of its
   * tube; past the gate is the interior), or in an interior (at most `margin` into the rock,
   * from the smooth walls). With `route`, also the nearest point of the interior's route.
   * Returns a shared object.
   */
  private caveAt(p: THREE.Vector3, margin: number, route: boolean): CaveHit | null {
    const out = _hit;
    for (let li = 0; li < this.links.length; li++) {
      const link = this.links[li];
      for (let k = 0; k < 2; k++) {
        const e = link.entrances[k];
        if (p.distanceToSquared(e.center) > e.bound2) continue;
        const near = nearestOnEntrance(e.entrance, p, e.entrance.radius + margin);
        if (!near) continue;
        const c = this.hollows[k === 0 ? link.def.from : link.def.to].center;
        out.link = li;
        out.end = k;
        out.at = near.at;
        out.depth = p.distanceTo(c) - groundRadius(link.ends[k], p, c);
        out.space = -1;
        return out;
      }
      const box = link.cave;
      if (p.x < box.min.x || p.y < box.min.y || p.z < box.min.z) continue;
      if (p.x > box.max.x || p.y > box.max.y || p.z > box.max.z) continue;
      const x = p.x - link.origin.x;
      const y = p.y - link.origin.y;
      const z = p.z - link.origin.z;
      const s = link.cave.sdf.sample(x, y, z, _sample);
      if (s.owner < 0 || s.phi > margin) continue;
      out.link = li;
      out.end = -1;
      out.at = route ? nearestPoint(link.route, x, y, z) : -1;
      out.depth = INTERIOR_DEPTH;
      out.space = link.spaceOfVoid[s.owner];
      return out;
    }
    return null;
  }

  /**
   * Each tunnel is clipped where the ground is drawn at its mouth (caveMaterial's
   * meshGroundNode): tell it the level of the ground tile there, and how far it has morphed.
   * Only the current world's ground is drawn (after its frame() picked the tiles).
   */
  private fitMouthsToGround(cameraUniverse: THREE.Vector3): void {
    for (const link of this.links) {
      if (!link.group.visible) continue;
      link.ends.forEach((end, k) => {
        const s = this.scenes[k === 0 ? link.def.from : link.def.to];
        if (s !== this.current) return;
        dirToFace(link.mouths[k].dir, _fc);
        const d = s.tree.drawnAt(_fc.face, _fc.u, _fc.v);
        if (!d) return;
        end.groundCells.value = s.terrain.cellsAt(d.node.level);
        const dist = cameraUniverse.distanceTo(link.mouthPoints[k]);
        end.groundMorph.value = Math.min(1, Math.max(0, (dist - d.morphStart) / (d.morphEnd - d.morphStart)));
      });
    }
  }

  /** Switch the world that's drawn (and lit, and heard) to hollow i. */
  switchTo(i: number): void {
    // Arriving before its shaders finished warming: show it now (anything still compiling
    // appears a moment later).
    if (this.warming?.index === i) this.endWarmup();
    const from = this.current;
    const to = this.scenes[i];
    // Arriving somewhere not loaded (a teleport): start at once (see waitingFor).
    void to.load();
    const lit = from.lanternLit;
    from.setActive(false);
    to.setActive(true);
    to.setLantern(lit);
    to.adoptExposure(from.currentExposure);
    this.active = i;
    this.showCaves();
  }

  frame(camera: THREE.PerspectiveCamera, cameraUniverse: THREE.Vector3, dt: number): void {
    for (const link of this.links) {
      if (!link.group.visible) continue;
      this.origin.toRender(this.hollows[link.def.from].center, link.ends[0].centerRel.value);
      this.origin.toRender(this.hollows[link.def.to].center, link.ends[1].centerRel.value);
      this.origin.toRender(link.origin, link.originRel.value);
      link.ends[0].light.value = this.scenes[link.def.from].daylightShare(link.mouths[0].dir);
      link.ends[1].light.value = this.scenes[link.def.to].daylightShare(link.mouths[1].dir);
    }
    // Meshed cave chunks become meshes and colliders, a few milliseconds' worth per frame.
    this.mesher.pump(BUILD_BUDGET);
    const until = performance.now() + BUILD_BUDGET;
    for (const link of this.links) link.interior.update(Math.max(0, until - performance.now()));
    this.shutGates();
    this.current.setOpen(1 - smooth(2, 14, this.caveDepth));
    this.current.frame(camera, cameraUniverse, dt);
    this.fitMouthsToGround(cameraUniverse);
    this.stepWarmup();
    this.stepPrecompile();
    // Deep in a cave a fixed exposure suits the lantern and lichen; each hollow's own
    // exposure takes over as you climb out of its mouth. The eye follows the daylight as it
    // fades down the entrance (the rock keeps some of it for DAYLIGHT_DEPTH, and the light
    // leaking in for a few metres more), in steps of equal ratio: a sunlit world's exposure
    // is a fifth of the cave's.
    const open = 1 - smooth(2, ADAPT_DEPTH, this.caveDepth);
    const target = CAVE_EXPOSURE * (this.current.currentExposure / CAVE_EXPOSURE) ** open;
    this.exposure += (target - this.exposure) * (1 - Math.exp(-dt * 1.5));
  }

  /**
   * Start the other hollows' shaders in the background, nearest first (one cave away, then
   * two...), so walking into one doesn't wait for its shaders. Call once the game is running.
   */
  warmInBackground(): void {
    if (this.warmQueue) return;
    const order: number[] = [];
    const seen = new Set([this.active]);
    let frontier = [this.active];
    while (frontier.length > 0) {
      const next: number[] = [];
      for (const i of frontier) {
        for (const l of this.links) {
          const j = l.def.from === i ? l.def.to : l.def.to === i ? l.def.from : -1;
          if (j >= 0 && !seen.has(j)) {
            seen.add(j);
            order.push(j);
            next.push(j);
          }
        }
      }
      frontier = next;
    }
    this.warmQueue = order;
    // Their compute shaders too (terrain tiles, air), so loading one later never stalls.
    this.precompileQueue = [...order];
  }

  /** One material of one waiting hollow (and its caves) per frame (see BackgroundWarmup). */
  private stepWarmup(): void {
    if (!this.warming) {
      const i = this.warmQueue?.shift();
      if (i === undefined || i === this.active) return;
      const s = this.scenes[i];
      const caves = this.links
        .filter((l) => CaveWorld.opensInto(l, i) && !l.group.visible)
        .flatMap((l) => [l.group, l.glow.lights]);
      // Its objects wait on a hidden layer, so showing the groups draws nothing yet.
      const warm = new BackgroundWarmup([s.group, s.lightGroup, ...caves]);
      for (const g of [s.group, s.lightGroup, ...caves]) g.visible = true;
      this.warming = { index: i, warm, caves };
    }
    this.warming.warm.step();
    if (this.warming.warm.done) this.endWarmup();
  }

  private endWarmup(): void {
    const w = this.warming;
    if (!w) return;
    w.warm.restore();
    if (w.index !== this.active) this.scenes[w.index].setActive(false);
    this.warming = null;
    this.showCaves();
  }

  describeTime(p: THREE.Vector3): string {
    return this.sceneAt(p).describeTime(p);
  }

  describeShape(p: THREE.Vector3): { size: string; middle: string } | null {
    return this.sceneAt(p).describeShape(p);
  }

  private sceneAt(p: THREE.Vector3): HollowScene {
    const h = this.hollowAt(p);
    const i = h ? this.hollows.indexOf(h) : -1;
    return i >= 0 ? this.scenes[i] : this.current;
  }

  hollowAt(p: THREE.Vector3): HollowDef | null {
    // Down a cave (below the ground at its mouth) you're in no world.
    const hit = this.caveAt(p, 0.5, false);
    if (hit && (hit.end < 0 || hit.depth > 0.2)) return null;
    for (const h of this.hollows) if (p.distanceTo(h.center) < h.radius + 50) return h;
    return null;
  }

  /** In a cave's air (not solid), or null: ask the hollows. */
  solidAt(p: THREE.Vector3): boolean | null {
    for (const link of this.links) {
      for (const e of link.entrances) {
        if (p.distanceToSquared(e.center) > e.bound2) continue;
        if (nearestOnEntrance(e.entrance, p, e.entrance.radius)) return false;
      }
      const box = link.cave;
      if (p.x < box.min.x || p.y < box.min.y || p.z < box.min.z) continue;
      if (p.x > box.max.x || p.y > box.max.y || p.z > box.max.z) continue;
      const x = p.x - link.origin.x;
      const y = p.y - link.origin.y;
      const z = p.z - link.origin.z;
      const sdf = link.cave.sdf;
      const cands = sdf.near(x, y, z);
      if (cands.length > 0 && sdf.distance(x, y, z, cands) < 0) return false;
    }
    return null;
  }

  /** For the HUD, in a cave: e.g. "Chasm in the cave between Hearth and Cinder · Hearth 60 m · Cinder 85 m". */
  describeCave(p: THREE.Vector3): string | null {
    const hit = this.caveAt(p, 1.5, true);
    if (!hit) return null;
    const link = this.links[hit.link];
    const [a, b] = [this.hollows[link.def.from].name, this.hollows[link.def.to].name];
    const between = `the cave between ${a} and ${b}`;
    if (hit.end >= 0) {
      return `In ${between} · ${Math.max(0, hit.depth).toFixed(0)} m down from ${hit.end === 0 ? a : b}`;
    }
    const space = link.layout.spaces[hit.space];
    const kind = SPACE_NAMES[space?.kind ?? 'passage'];
    const toA = link.routeAt[hit.at] + link.entrances[0].length;
    const toB = link.routeLength - link.routeAt[hit.at] + link.entrances[1].length;
    return `${kind[0].toUpperCase()}${kind.slice(1)} in ${between} · ${a} ${metres(toA)} · ${b} ${metres(toB)}`;
  }

  groundAltitude(p: THREE.Vector3): number | null {
    const h = this.hollowAt(p);
    return h ? this.scenes[this.hollows.indexOf(h)].groundAltitude(p) : null;
  }

  /** The cave mouths of the hollow at p: where each is, where it leads, distance over the ground. */
  private mouthsNear(p: THREE.Vector3): { point: THREE.Vector3; to: string; distance: number }[] {
    const h = this.hollowAt(p);
    if (!h) return [];
    const i = this.hollows.indexOf(h);
    const out: { point: THREE.Vector3; to: string; distance: number }[] = [];
    for (const m of this.mouthInfo) {
      if (m.hollow !== i) continue;
      const distance = _g.copy(p).sub(h.center).normalize().angleTo(m.dir) * h.radius;
      out.push({ point: m.point, to: m.to, distance });
    }
    return out;
  }

  /** Where the caves out of the current hollow are, e.g. "cave to Emberdeep 7.3 km". */
  guide(p: THREE.Vector3): string | null {
    const mouths = this.mouthsNear(p);
    return mouths.length > 0 ? mouths.map((m) => `cave to ${m.to} ${metres(m.distance)}`).join(' · ') : null;
  }

  /** On-screen markers for the cave mouths of the current hollow. */
  waypoints(p: THREE.Vector3): { point: THREE.Vector3; label: string }[] {
    return this.mouthsNear(p).map((m) => ({ point: m.point, label: `${m.to} ${metres(m.distance)}` }));
  }

  loadProgress(): number {
    return this.scenes[this.home].loadProgress();
  }

  settled(): boolean {
    return this.current.settled();
  }

  toggleLantern(): void {
    this.current.toggleLantern();
    this.autoLantern = false;
  }

  get lanternLit(): boolean {
    return this.current.lanternLit;
  }

  holdLantern(at: THREE.Vector3 | null): void {
    for (const s of this.scenes) s.holdLantern(at);
  }

  /** The hollow's bounce light, dimming down an entrance as the cave's rock does (DAYLIGHT_DEPTH). */
  heroLight(p: THREE.Vector3, out: THREE.Color): THREE.Color {
    return this.current.heroLight(p, out).multiplyScalar(1 - smooth(0, DAYLIGHT_DEPTH, this.caveDepth));
  }

  heroSun(p: THREE.Vector3, dir: THREE.Vector3, out: THREE.Color): THREE.Color {
    return this.current.heroSun(p, dir, out).multiplyScalar(1 - smooth(0, DAYLIGHT_DEPTH, this.caveDepth));
  }

  stats(): Record<string, string> {
    const z = this.zone;
    let where = 'outside';
    if (z.link >= 0) {
      const link = this.links[z.link];
      if (z.end >= 0)
        where = `in ${link.name}, entrance ${z.end === 0 ? 'A' : 'B'} (${z.at.toFixed(0)}/${link.entrances[z.end].entrance.path.length})`;
      else
        where = `in ${link.name}, ${link.layout.spaces[z.space]?.kind ?? '?'} (${z.at}/${link.layout.route.length})`;
    }
    const ready = this.links.filter((l) => l.interior.state === 'ready').length;
    const worlds = this.scenes
      .map((s) => `${s.name}${s.loadState === 'loaded' ? '' : s.loadState === 'loading' ? ' …' : ' ×'}`)
      .join(' ');
    return {
      ...this.current.stats(),
      cave: `${this.current.name} side · ${where} · interiors ${ready}/${this.links.length} ready`,
      worlds,
    };
  }
}

/**
 * The part of a triangle mesh where `depth` ≥ 0 (in the rock): triangles wholly above are
 * dropped, and those across the ground are cut along it (where their edges cross it, by
 * linear interpolation; each crossing made once, so neighbours share it). Keeping whole
 * triangles that stood partly out of the ground left a lip at the sides of a cave mouth,
 * up to a triangle high, that caught your feet: nothing past it to step up onto, and no
 * way down into the hole.
 */
function clipBelow(
  positions: Float32Array,
  indices: ArrayLike<number>,
  depth: (x: number, y: number, z: number) => number,
): { positions: Float32Array; indices: Uint32Array } {
  const out: number[] = Array.from(positions);
  const d: number[] = [];
  for (let i = 0; i < positions.length; i += 3)
    d.push(depth(positions[i], positions[i + 1], positions[i + 2]));
  const crossings = new Map<number, number>();
  const n = d.length;
  const cross = (a: number, b: number) => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const key = lo * n + hi;
    let v = crossings.get(key);
    if (v === undefined) {
      const t = d[lo] / (d[lo] - d[hi]);
      for (let k = 0; k < 3; k++) out.push(out[lo * 3 + k] + (out[hi * 3 + k] - out[lo * 3 + k]) * t);
      v = out.length / 3 - 1;
      crossings.set(key, v);
    }
    return v;
  };
  const idx: number[] = [];
  for (let i = 0; i < indices.length; i += 3) {
    const tri = [indices[i], indices[i + 1], indices[i + 2]];
    const below = tri.filter((v) => d[v] >= 0).length;
    if (below === 3) {
      idx.push(tri[0], tri[1], tri[2]);
      continue;
    }
    if (below === 0) continue;
    // Around the triangle, keep the corners below and add the crossings (Sutherland–Hodgman).
    const poly: number[] = [];
    for (let e = 0; e < 3; e++) {
      const a = tri[e];
      const b = tri[(e + 1) % 3];
      if (d[a] >= 0) poly.push(a);
      if (d[a] >= 0 !== d[b] >= 0) poly.push(cross(a, b));
    }
    for (let k = 1; k + 1 < poly.length; k++) idx.push(poly[0], poly[k], poly[k + 1]);
  }
  return { positions: new Float32Array(out), indices: new Uint32Array(idx) };
}

/** A disc across an entrance at its gate, facing back up the entrance (relative to the gate). */
function gateCap(e: Entrance) {
  const n = new THREE.Vector3();
  const b = new THREE.Vector3();
  perpendicularBasis(e.gateDir, n, b);
  const SEGMENTS = 28;
  const r = e.radius + 0.3;
  const positions = new Float32Array((SEGMENTS + 2) * 3);
  const normals = new Float32Array((SEGMENTS + 2) * 3);
  for (let i = 0; i <= SEGMENTS; i++) {
    const t = (i / SEGMENTS) * Math.PI * 2;
    const k = (i + 1) * 3;
    positions[k] = r * (Math.cos(t) * n.x + Math.sin(t) * b.x);
    positions[k + 1] = r * (Math.cos(t) * n.y + Math.sin(t) * b.y);
    positions[k + 2] = r * (Math.cos(t) * n.z + Math.sin(t) * b.z);
  }
  for (let i = 0; i < SEGMENTS + 2; i++) normals.set([-e.gateDir.x, -e.gateDir.y, -e.gateDir.z], i * 3);
  // Wound to face back up the entrance (n × b is the gate's direction).
  const indices = new Uint32Array(SEGMENTS * 3);
  for (let i = 0; i < SEGMENTS; i++) indices.set([0, i + 2, i + 1], i * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeBoundingSphere();
  return { geometry, positions, indices };
}

/** An entrance's tube: its path, then on past the gate a little, a few centimetres wider. */
function entranceTube(e: Entrance): { path: THREE.Vector3[]; radii: number[] } {
  const path = [...e.path];
  const radii = path.map(() => e.radius);
  for (const t of [0.02, GATE_OVERLAP]) {
    path.push(e.gate.clone().addScaledVector(e.gateDir, t));
    radii.push(e.radius + GATE_FLARE);
  }
  return { path, radii };
}

function entranceInfo(entrance: Entrance): EntranceInfo {
  const center = new THREE.Vector3();
  for (const p of entrance.path) center.add(p);
  center.divideScalar(entrance.path.length);
  let far = 0;
  let length = 0;
  entrance.path.forEach((p, i) => {
    far = Math.max(far, p.distanceTo(center));
    if (i > 0) length += p.distanceTo(entrance.path[i - 1]);
  });
  const bound = far + entrance.radius + 3;
  return { entrance, center, bound2: bound * bound, length };
}

/** Index of the point of a flat (x, y, z, …) list nearest to (x, y, z). */
function nearestPoint(points: Float64Array, x: number, y: number, z: number): number {
  let best = Number.POSITIVE_INFINITY;
  let bi = 0;
  for (let i = 0; i < points.length; i += 3) {
    const dx = points[i] - x;
    const dy = points[i + 1] - y;
    const dz = points[i + 2] - z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < best) {
      best = d;
      bi = i / 3;
    }
  }
  return bi;
}

function metres(d: number): string {
  return d < 1000 ? `${Math.round(d)} m` : `${(d / 1000).toFixed(1)} km`;
}

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(Math.max((x - e0) / (e1 - e0), 0), 1);
  return t * t * (3 - 2 * t);
}
